import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';

const LinedCanvas = ({ width = 400, height = 400, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [convertedText, setConvertedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const drawColor = 'black'; 

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Draw horizontal lines every 30 pixels
    ctx.strokeStyle = 'lightgray'; 
    ctx.lineWidth = 0.5; 
    
    for (let y = 30; y < height; y += 30) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const startDrawing = (x, y) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };
    
    const draw = (x, y) => {
      if (!isDrawing) return;
      ctx.strokeStyle = drawColor;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath(); 
      }
    };

    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleMouseMove = (e) => {
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;

    const handleTouchStart = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleTouchEnd = endDrawing;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing, drawColor]);

  const handleUploadImage = async () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL('image/png');
      const response = await fetch(dataURL);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append('image', blob, 'canvas-image.png');
      await axios.post('http://localhost:5000/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Image uploaded successfully!');
    }
  };

  const handleConvertToText = () => {
    setLoading(true);
    const canvas = canvasRef.current;
    const imageDataURL = canvas.toDataURL();

    fetch('https://api.ocr.space/parse/image', {
      method: 'POST',
      body: JSON.stringify({ base64Image: imageDataURL }),
      headers: {
        'Content-Type': 'application/json',
        'apikey': 'K81755459088957', 
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.IsErroredOnProcessing) {
          setError(data.ErrorMessage);
          setConvertedText('');
        } else {
          setConvertedText(data.ParsedResults[0]?.ParsedText || 'No text found');
          setError('');
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setError('An error occurred while processing the image.');
        setConvertedText('');
        setLoading(false);
      });
  };

  const handleSaveText = async () => {
    try {
      await axios.post('http://localhost:5000/save-text', { text: convertedText });
      alert('Text extracted and saved successfully!');
    } catch (error) {
      console.error('Error saving text:', error);
      alert('Failed to save text.');
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  return (
    <div style={{ marginTop: '250px', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={width}  
        height={height} 
        style={{ 
          border: '1px solid #ccc', 
          backgroundColor: 'white', 
          display: 'block', 
          margin: '0 auto' 
        }}
        className="canvas"
      />
      <div className='flex flex-row px-2 mt-2'>
        <button className="ml-2" onClick={clearCanvas}>Clear</button>
        <button className="ml-2" onClick={handleUploadImage}>Upload Image</button>
        <button className="ml-2" onClick={handleConvertToText}>Extract Text</button>
      </div>
      
      {/* Display Converted Text */}
      <div style={{ marginTop: '10px', marginLeft: '10px' }}>
        <h2 className='px-2'>Converted Text (Editable):</h2>
        <textarea
          value={convertedText}
          onChange={(e) => setConvertedText(e.target.value)}
          className='text-black bg-white'
          style={{ maxWidth: '100%', height: '200px', fontSize: '12px' }}
        />
      </div>
      <div className='flex flex-row mt-0 px-2'>
        <button onClick={handleSaveText}>Save Text</button>
        <button className="ml-2" onClick={onClose}>Close</button>
      </div>
      <div className='flex flex-col justify-center'>
        {loading && <p>Loading...</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </div>
    </div>
  );
};

export default LinedCanvas;
